import { DEFAULT_ALLOW_FROM, DEFAULT_EXPECTED_TO, DEFAULT_VALID_ORIGIN_RES } from './constants';
import { rocketIntegrationLogger } from './logger';
import { isRiRequest, isRiResponse } from './types';

const logger = rocketIntegrationLogger;

/**
 * Parse window event. If the event does not contain a message specific to
 * integration rocket return null
 */
export function processMessageEvent(
  event: MessageEvent,
  expectedTo = DEFAULT_EXPECTED_TO,
  allowFrom = DEFAULT_ALLOW_FROM,
  validOriginRes = DEFAULT_VALID_ORIGIN_RES
) {
  // Ignore if not for expected origin
  if (!validOriginRes.some((x) => x.test(event.origin))) return null;

  // Ignore if its not a string
  if (typeof event.data !== 'string') return null;

  // Try to parse as JSON. If it can't be, don't error out (it might be
  // for something else), just ignore.
  let message;
  try {
    message = JSON.parse(event.data);
  } catch {
    return null;
  }

  // At a minimum we expect it to have a to and from field, if it doesn't,
  // then ignore
  if (!('to' in message) || !message.to || !('from' in message) || !message.from) return null;
  const to = message.to;
  const from = message.from;

  // Now check to and from. If they have values but don't match its worth
  // reporting for debugging purposes
  if (to !== expectedTo) {
    logger.warn(
      `Window message received: to '${to}' is different than expected value '${expectedTo}'. Ignoring...`,
      event
    );
    return null;
  }
  if (!allowFrom.includes(from)) {
    logger.warn(
      `Window message received: from: '${from}' is not in allow list '${allowFrom}'. Ignoring...`,
      event
    );
    return null;
  }

  // Either request or response
  if ('request' in message) {
    const request = message.request;
    if (isRiRequest(request)) {
      logger.debug(
        `Received '${request.name}' request from rocket (requestId=${request.requestId})`,
        request,
        message
      );

      return { request, from: message.from };
    } else {
      return null;
    }
  }

  if ('response' in message) {
    const response = message.response;
    if (isRiResponse(response)) {
      logger.debug(
        `Received response from rocket (requestId=${response.requestId})`,
        response,
        message
      );
      return { response, from: message.from };
    } else {
      return null;
    }
  }

  logger.error(
    `Window message received from ${from} missing both request and response fields`,
    message
  );

  return null;
}
